/**
 * Language primitives for the proposed-answer language control.
 *
 * Shared by Venus and Neptune so both surfaces offer the same languages under
 * the same labels and reduce codes the same way (spec rules 2.5, 2.6, 2.7).
 */

/**
 * Rule 2.5: codes are reduced to their base language. Regional variants are not
 * distinguished anywhere in this feature, so "en-GB", "en_US" and "EN" all
 * collapse to "en" — that is what gets selected, persisted, compared and sent.
 */
export function baseLanguage(code: string | null | undefined): string | null {
  if (!code) return null;
  const base = code.trim().toLowerCase().split(/[-_]/)[0];
  return base && /^[a-z]{2,3}$/.test(base) ? base : null;
}

/** Rule 2.5 applied to a comparison: are these the same language? */
export function sameLanguage(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = baseLanguage(a);
  const right = baseLanguage(b);
  return left !== null && left === right;
}

/** The language selected when nothing else is known (rule 2.2, last resort). */
export const DEFAULT_ANSWER_LANGUAGE = "de";

/**
 * The fixed set of offered languages (rule 2.6). The mail's detected language is
 * appended to this when it is not already a member, so an operator answering in
 * a language outside the set can still pick it.
 */
export const OFFERED_ANSWER_LANGUAGES: readonly string[] = [
  "de",
  "en",
  "fr",
  "it",
  "es",
  "nl",
  "pt",
  "pl",
  "tr",
];

/**
 * Label for a language code, in `uiLanguage`.
 *
 * This used to be a hardcoded map of German names, which left the control
 * reading "Französisch" on an English UI. Intl.DisplayNames localises the name
 * into whatever the operator is working in and covers every language rather than
 * the couple of dozen the map happened to list, so there is no table to keep in
 * step with OFFERED_ANSWER_LANGUAGES.
 *
 * Falls back to the upper-cased code: Intl returns the code unchanged for one it
 * cannot name, and a bare "XH" is more honest than a wrong name.
 */
export function answerLanguageLabel(
  code: string | null | undefined,
  uiLanguage = "de",
): string {
  const base = baseLanguage(code);
  if (!base) return "";
  try {
    const name = new Intl.DisplayNames([uiLanguage], { type: "language" }).of(base);
    if (name && name.toLowerCase() !== base) return name;
  } catch {
    // Invalid uiLanguage tag, or an environment without this Intl subset.
  }
  return base.toUpperCase();
}

/**
 * Rule 2.6: the fixed set, plus the mail's detected language appended when it is
 * not among them. Base-reduced and deduplicated, fixed set first in its declared
 * order so the list does not reshuffle between mails.
 */
export function answerLanguageOptions(
  detectedLanguage: string | null | undefined,
): string[] {
  const options = [...OFFERED_ANSWER_LANGUAGES];
  const detected = baseLanguage(detectedLanguage);
  if (detected && !options.includes(detected)) options.push(detected);
  return options;
}
